import React, { useEffect, useCallback } from 'react'
import { Box, Stack, Text } from '@chakra-ui/react'
import { AutoSuggest, LabeledInput, LabeledSelect, Checkbox } from '@apex/component-library'
import { FormikErrors, FormikTouched } from 'formik'
import { LocationOptions, BeneficiaryTypes } from '../../containers/WireTransfers/Types'
import { BankFormCopy } from './BankFormTypes'
import { WireTransferValues } from '../../containers/WireTransfers/Types'

interface FormikValues {
  bankDestination?: string
  bankRouting?: string
  beneficiaryType?: BeneficiaryTypes
  bankName?: string
  bankCity?: string
  bankState?: string
  bankPostalCode?: string
  bankCountry?: string
  thirdPartyLocation?: LocationOptions
  thirdPartyCountry?: string
  thirdPartyAddress?: string
  thirdPartyCity?: string
  thirdPartyState?: string
  thirdPartyZip?: string
  bankUseIntermediary: boolean
}

export interface WireBankFormProps {
  formikValues: WireTransferValues
  formikBlur: (e: React.FocusEvent<any>) => void
  formikTouched: FormikTouched<FormikValues>
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  setTouched: (fields: object, validate: boolean) => void
  errors: FormikErrors<FormikValues>
  countryList: { value: string }[]
  countryListLoading: boolean
  countryListFailedMessage?: string
}

const WireBankForm = ({
  formikValues,
  setFieldValue,
  formikTouched,
  formikBlur,
  errors,
  setTouched,
  countryList,
  countryListLoading,
  countryListFailedMessage
}: WireBankFormProps) => {
  const {
    bankDestination,
    bankRouting,
    beneficiaryType,
    bankName,
    bankCountry,
    bankCity,
    bankState,
    bankPostalCode,
    bankUseIntermediary
  } = formikValues

  const clearBankForm = () => {
    setFieldValue('bankName', '')
    setFieldValue('bankCity', '')
    setFieldValue('bankState', '')
    setFieldValue('bankPostalCode', '')
    setFieldValue('bankCountry', '')
    setFieldValue('bankCountryCode', '')
    setFieldValue('bankUseIntermediary', false)
    setTouched({}, true)
  }

  const clearBankFormMemo = useCallback(clearBankForm, [])

  useEffect(() => {
    setFieldValue('bankDestination', LocationOptions.Domestic)
  }, [setFieldValue, beneficiaryType])

  useEffect(() => {
    clearBankFormMemo()
    // FEECM-16401-v2: clearBankForm() wipes touched, so re-mark bankRouting to keep the
    // bank-code/destination error visible after switching. (Not a dep: would clear on keystroke.)
    if (bankRouting) {
      setTouched({ bankRouting: true }, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankDestination, clearBankFormMemo])

  useEffect(clearBankFormMemo, [clearBankFormMemo])

  const showIntermediateOption =
    (bankDestination === LocationOptions.Domestic && beneficiaryType === BeneficiaryTypes.AccountHolder) ||
    beneficiaryType === BeneficiaryTypes.ThirdParty

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Text as='h3' textStyle='h3'>
          {BankFormCopy.TitleBeneficiaryBank}
        </Text>
        {beneficiaryType === BeneficiaryTypes.ThirdParty && <Text>{BankFormCopy.TitleHelperText}</Text>}
      </Stack>
      <Stack>
        <Stack direction='row' spacing={8}>
          {beneficiaryType === BeneficiaryTypes.AccountHolder && (
            <LabeledSelect
              name='bankDestination'
              aria-label='bank destination'
              value={bankDestination}
              label={BankFormCopy.Destination}
              onChange={(e) => setFieldValue('bankDestination', e.target.value)}
              onBlur={formikBlur}
              isRequired
              errorMessage={errors?.bankDestination}
              isInvalid={formikTouched.bankDestination && !!errors?.bankDestination}
            >
              <option value={LocationOptions.Domestic}>{BankFormCopy.ValueDomestic}</option>
              <option value={LocationOptions.International}>{BankFormCopy.ValueInternational}</option>
            </LabeledSelect>
          )}
          {beneficiaryType === BeneficiaryTypes.ThirdParty && (
            <LabeledInput
              name='bankDestination'
              label={BankFormCopy.Destination}
              onChange={(e) => setFieldValue('bankDestination', e.target.value)}
              onBlur={formikBlur}
              errorMessage={errors?.bankDestination}
              isInvalid={formikTouched.bankDestination && !!errors?.bankDestination}
              value={BankFormCopy.ValueDomestic}
              isReadOnly
            />
          )}
          <LabeledInput
            name='bankRouting'
            label={
              bankDestination === LocationOptions.Domestic
                ? BankFormCopy.BankRoutingDomestic
                : BankFormCopy.BankRoutingInternational
            }
            onChange={(e) => setFieldValue('bankRouting', e.target.value)}
            isRequired
            errorMessage={errors?.bankRouting}
            isInvalid={formikTouched.bankRouting && !!errors?.bankRouting}
            onBlur={formikBlur}
            value={bankRouting}
          />
        </Stack>
        {showIntermediateOption && (
          <Checkbox
            name='bankAdditionalInformation'
            isChecked={bankUseIntermediary}
            onChange={(e) => {
              setFieldValue('bankUseIntermediary', e.target.checked)
            }}
          >
            {BankFormCopy.UseIntermediaryAccount}
          </Checkbox>
        )}
      </Stack>
      {bankDestination === LocationOptions.International && (
        <Stack data-testid={'first-party-int'}>
          <LabeledInput
            name='bankName'
            label={BankFormCopy.BankName}
            onChange={(e) => setFieldValue('bankName', e.target.value)}
            onBlur={formikBlur}
            value={bankName}
            isRequired
            errorMessage={errors?.bankName}
            isInvalid={formikTouched.bankName && !!errors?.bankName}
          />
          <Stack direction='row' spacing={8}>
            <LabeledInput
              name='bankCity'
              label={BankFormCopy.City}
              onChange={(e) => setFieldValue('bankCity', e.target.value)}
              onBlur={formikBlur}
              value={bankCity}
              isRequired
              errorMessage={errors?.bankCity}
              isInvalid={formikTouched.bankCity && !!errors?.bankCity}
            />
            <LabeledInput
              name='bankState'
              label={BankFormCopy.StateProv}
              onChange={(e) => setFieldValue('bankState', e.target.value)}
              onBlur={formikBlur}
              value={bankState}
              errorMessage={errors?.bankState}
              isInvalid={formikTouched.bankState && !!errors?.bankState}
            />
          </Stack>
          <Stack direction='row' spacing={8}>
            <LabeledInput
              name='bankPostalCode'
              label={BankFormCopy.PostalCode}
              onChange={(e) => setFieldValue('bankPostalCode', e.target.value)}
              onBlur={formikBlur}
              value={bankPostalCode}
              errorMessage={errors?.bankPostalCode}
              isInvalid={formikTouched.bankPostalCode && !!errors?.bankPostalCode}
            />
            <Box width='100%'>
              <AutoSuggest
                name='bankCountry'
                label='Country'
                aria-label='Beneficiary Bank Country'
                inputValue={bankCountry || ''}
                onBlur={formikBlur}
                onChange={(e) => {
                  setFieldValue('bankCountry', e.target.value)
                }}
                onSelectItem={(val) => setFieldValue('bankCountry', val)}
                isRequired
                dataSet={countryList}
                placeholder={countryListLoading ? 'Loading...' : ''}
                errorMessage={countryListFailedMessage || errors?.bankCountry}
                isInvalid={!!countryListFailedMessage || (formikTouched.bankCountry && !!errors?.bankCountry)}
              />
            </Box>
          </Stack>

          <Checkbox
            name='bankUseIntermediary'
            isChecked={bankUseIntermediary}
            onChange={(e) => {
              setFieldValue('bankUseIntermediary', e.target.checked)
            }}
          >
            {BankFormCopy.UseIntermediaryAccount}
          </Checkbox>
        </Stack>
      )}
    </Stack>
  )
}

export default WireBankForm
